import { ConnectedUserInfosResponse } from 'api/account';
import { BankAccountDto } from 'api/bank-account';
import { Button } from 'components/ui/Button';
import { BankAccount } from 'components/domain/Bank/BankAccount';
import { BankAccountCreateDialog } from 'components/domain/Bank/BankAccountCreateDialog';
import { Dialog } from 'components/feedback/Dialog/Dialog';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay/ConfirmationOverlay';
import { InputText } from 'components/forms/InputText';
import { Sizes } from 'enums/sizes';
import { FC, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { accountsAPI, bankAccountsAPI } from 'services/swagger-api';

const UserAccount: FC = () => {
  const [userInfo, setUserInfo] = useState<ConnectedUserInfosResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const [bankAccounts, setBankAccounts] = useState<BankAccountDto[]>([]);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<string | undefined>();
  const [isLoadingBankAccounts, setIsLoadingBankAccounts] = useState(false);
  const [showAddBankAccount, setShowAddBankAccount] = useState(false);
  const [pendingDefaultAccount, setPendingDefaultAccount] = useState<BankAccountDto | null>(null);
  const [editingBankAccount, setEditingBankAccount] = useState<BankAccountDto | null>(null);
  const [editBankAccountData, setEditBankAccountData] = useState({ iban: '', label: '', bic: '' });
  const [isUpdatingBankAccount, setIsUpdatingBankAccount] = useState(false);

  useEffect(() => {
    loadUserInfo();
    loadBankAccounts();
  }, []);

  const loadUserInfo = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await accountsAPI.getUserConnectedInfo();
      setUserInfo(response);
    } catch (err) {
      setError('Erreur lors du chargement des informations utilisateur');
      console.error('Error loading user info:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadBankAccounts = useCallback(async () => {
    try {
      setIsLoadingBankAccounts(true);
      const response = await bankAccountsAPI.getMyBankAccounts();
      if (response.isSuccess) {
        setBankAccounts(response.bankAccounts);
        const defaultAccount = response.bankAccounts.find(a => a.isDefault);
        if (defaultAccount) setSelectedBankAccountId(defaultAccount.id);
      }
    } catch (err) {
      console.error('Error loading bank accounts:', err);
    } finally {
      setIsLoadingBankAccounts(false);
    }
  }, []);

  const handleSelectBankAccount = useCallback((bankAccount: BankAccountDto) => {
    if (bankAccount.id === selectedBankAccountId) return;
    setPendingDefaultAccount(bankAccount);
  }, [selectedBankAccountId]);

  const confirmSelectBankAccount = useCallback(async () => {
    if (!pendingDefaultAccount) return;
    setSelectedBankAccountId(pendingDefaultAccount.id);
    try {
      const result = await bankAccountsAPI.updateBankAccount({
        id: pendingDefaultAccount.id,
        updateBankAccountCommand: {
          iban: pendingDefaultAccount.iban,
          bic: pendingDefaultAccount.bic ?? undefined,
          label: pendingDefaultAccount.label,
          isDefault: true
        }
      });
      if (!result.isSuccess) {
        toast.error(result.errorMessage || 'Erreur lors de la sélection du compte');
        loadBankAccounts();
      }
    } catch (err) {
      toast.error('Erreur lors de la sélection du compte');
      loadBankAccounts();
    }
    setPendingDefaultAccount(null);
  }, [pendingDefaultAccount, loadBankAccounts]);

  const handleDeleteBankAccount = useCallback(async (bankAccountId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce compte bancaire ?')) return;
    try {
      const result = await bankAccountsAPI.deleteBankAccount({ id: bankAccountId });
      if (result.isSuccess) {
        loadBankAccounts();
      } else {
        toast.error(result.errorMessage || 'Erreur lors de la suppression');
      }
    } catch (err) {
      toast.error('Erreur lors de la suppression du compte');
    }
  }, [loadBankAccounts]);

  const handleEditBankAccount = useCallback((bankAccount: BankAccountDto) => {
    setEditingBankAccount(bankAccount);
    const formattedIban = bankAccount.iban.replace(/(.{4})/g, '$1  ').trim();
    setEditBankAccountData({
      iban: formattedIban,
      label: bankAccount.label,
      bic: bankAccount.bic ?? ''
    });
  }, []);

  const handleUpdateBankAccount = async () => {
    if (!editingBankAccount) return;
    if (!editBankAccountData.iban || !editBankAccountData.label) {
      toast.error("L'IBAN et le libellé sont requis");
      return;
    }
    try {
      setIsUpdatingBankAccount(true);
      const cleanIban = editBankAccountData.iban.replace(/\s/g, '');
      const result = await bankAccountsAPI.updateBankAccount({
        id: editingBankAccount.id,
        updateBankAccountCommand: {
          iban: cleanIban,
          label: editBankAccountData.label,
          bic: editBankAccountData.bic || undefined,
          isDefault: editingBankAccount.isDefault
        }
      });
      if (result.isSuccess) {
        setEditingBankAccount(null);
        loadBankAccounts();
      } else {
        toast.error(result.errorMessage || 'Erreur lors de la modification du compte');
      }
    } catch (err) {
      toast.error('Erreur lors de la modification du compte');
    } finally {
      setIsUpdatingBankAccount(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess(false);

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmNewPassword) {
      setPasswordError('Tous les champs sont requis');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      setIsUpdatingPassword(true);
      const response = await accountsAPI.updatePassword({
        updatePasswordCommand: passwordData
      });

      if (response.isSuccess) {
        setPasswordSuccess(true);
        setPasswordData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
        setTimeout(() => {
          setIsChangingPassword(false);
          setPasswordSuccess(false);
        }, 2000);
      } else {
        setPasswordError(response.errorMessage || 'Erreur lors du changement de mot de passe');
      }
    } catch (err) {
      setPasswordError('Erreur lors du changement de mot de passe');
      console.error('Error updating password:', err);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center h-96">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200"></div>
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent absolute top-0"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !userInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl border border-red-200 p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Erreur de chargement</h3>
                <p className="text-gray-600">{error || 'Impossible de charger les informations'}</p>
              </div>
            </div>
            <Button color="primary" onClick={loadUserInfo} className="mt-4">
              Réessayer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* En-tête avec avatar */}
        <div className="bg-gradient-to-r from-primary to-primary/80 rounded-2xl shadow-xl p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-3xl font-bold border-4 border-white/30">
              {userInfo.firstName.charAt(0)}{userInfo.lastName.charAt(0)}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2">
                {userInfo.firstName} {userInfo.lastName}
              </h1>
              <p className="text-white/90 text-lg">{userInfo.email}</p>
              <div className="flex gap-3 mt-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {userInfo.legalEntity}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Informations personnelles */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-50 to-white p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Informations personnelles
                </h2>
              </div>
            </div>
            <div className="p-6 space-y-5">
              <InfoField
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                }
                label="Nom d'utilisateur"
                value={userInfo.userName}
              />
              <InfoField
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                }
                label="Email"
                value={userInfo.email}
              />
              <InfoField
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                }
                label="Téléphone"
                value={userInfo.phone}
              />
              <InfoField
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                }
                label="Adresse"
                value={
                  <>
                    <p>{userInfo.address.line1}</p>
                    {userInfo.address.line2 && <p>{userInfo.address.line2}</p>}
                    {userInfo.address.line3 && <p>{userInfo.address.line3}</p>}
                    <p>{userInfo.address.zipCode} {userInfo.address.city}</p>
                    <p className="text-gray-600">{userInfo.address.country}</p>
                  </>
                }
              />
            </div>
          </div>

          {/* Informations entreprise */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-50 to-white p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Informations entreprise
                </h2>
              </div>
            </div>
            <div className="p-6 space-y-5">
              <InfoField
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                }
                label="Entité légale"
                value={userInfo.legalEntity}
                highlight
              />
              <InfoField
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
                label="SIRET"
                value={userInfo.siret}
              />
              <InfoField
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                }
                label="Plateforme"
                value={userInfo.currentPlateforme}
              />
            </div>
          </div>
        </div>

        {/* Comptes bancaires */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-50 to-white p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Comptes bancaires
                </h2>
              </div>
              <Button
                color="primary"
                onClick={() => setShowAddBankAccount(true)}
              >
                Ajouter un compte
              </Button>
            </div>
          </div>

          <div className="p-6">
            {isLoadingBankAccounts ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-primary"></div>
              </div>
            ) : bankAccounts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <p>Aucun compte bancaire enregistré</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {bankAccounts.map(account => (
                  <BankAccount
                    key={account.id}
                    bankAccount={account}
                    isSelected={account.id === selectedBankAccountId}
                    onSelect={handleSelectBankAccount}
                    onDelete={handleDeleteBankAccount}
                    onEdit={handleEditBankAccount}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal ajout compte bancaire */}
        <BankAccountCreateDialog
          open={showAddBankAccount}
          onClose={() => setShowAddBankAccount(false)}
          onSuccess={loadBankAccounts}
          isFirstAccount={bankAccounts.length === 0}
        />

        {/* Confirmation changement de compte par défaut */}
        <ConfirmationOverlay
          isOpen={!!pendingDefaultAccount}
          onClose={() => setPendingDefaultAccount(null)}
          onConfirm={confirmSelectBankAccount}
          title="Changer le compte par défaut"
          description="Vos commissions seront désormais versées sur ce compte. Voulez-vous continuer ?"
          variant="warning"
        />

        {/* Modal modification compte bancaire */}
        <Dialog open={!!editingBankAccount} onClose={() => setEditingBankAccount(null)} size={Sizes.md}>
          <h1 className="font-20 text-dark font-w700 text-center mb-4">
            Modification du compte bancaire
          </h1>
          <div className="flex flex-col gap-6">
            <InputText
              label="Libellé"
              required
              type="text"
              value={editBankAccountData.label}
              onChange={(e) => setEditBankAccountData({ ...editBankAccountData, label: e.target.value })}
              disabled={isUpdatingBankAccount}
            />
            <InputText
              label="IBAN"
              required
              type="text"
              value={editBankAccountData.iban}
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                const formattedValue = value.replace(/(.{4})/g, '$1  ').trim();
                setEditBankAccountData({ ...editBankAccountData, iban: formattedValue });
              }}
              placeholder="FR00 0000 0000 0000 0000 0000 000"
              disabled={isUpdatingBankAccount}
            />
            <InputText
              label="BIC"
              type="text"
              value={editBankAccountData.bic}
              onChange={(e) => setEditBankAccountData({ ...editBankAccountData, bic: e.target.value.toUpperCase() })}
              placeholder="BNPAFRPP"
              disabled={isUpdatingBankAccount}
            />
            <div className="flex grow gap-4 justify-center">
              <Button
                size={Sizes.lg}
                outline
                onClick={() => setEditingBankAccount(null)}
                disabled={isUpdatingBankAccount}
              >
                Annuler
              </Button>
              <Button
                color="primary"
                size={Sizes.lg}
                onClick={handleUpdateBankAccount}
                disabled={isUpdatingBankAccount}
              >
                {isUpdatingBankAccount ? 'Modification en cours...' : 'Valider'}
              </Button>
            </div>
          </div>
        </Dialog>

        {/* Sécurité */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-50 to-white p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Sécurité du compte
              </h2>
            </div>
          </div>

          <div className="p-6">
            {passwordSuccess && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-green-800 font-medium">Mot de passe modifié avec succès !</p>
              </div>
            )}

            {!isChangingPassword ? (
              <div className="flex items-center justify-between bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Mot de passe</h3>
                    <p className="text-sm text-gray-600">
                    Modifier votre mot de passe
                    </p>
                  </div>
                </div>
                <Button
                  color="primary"
                  onClick={() => setIsChangingPassword(true)}
                >
                  Modifier
                </Button>
              </div>
            ) : (
              <div className="space-y-5">
                <InputText
                  type="password"
                  label="Mot de passe actuel"
                  required
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  disabled={isUpdatingPassword}
                />
                <InputText
                  type="password"
                  label="Nouveau mot de passe"
                  required
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  disabled={isUpdatingPassword}
                />
                <InputText
                  type="password"
                  label="Confirmer le nouveau mot de passe"
                  required
                  value={passwordData.confirmNewPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmNewPassword: e.target.value })}
                  error={passwordError}
                  disabled={isUpdatingPassword}
                />

                {passwordError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-red-800">{passwordError}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button
                    color="primary"
                    onClick={handlePasswordChange}
                    disabled={isUpdatingPassword}
                    className="flex-1"
                  >
                    {isUpdatingPassword ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Modification en cours...
                      </span>
                    ) : (
                      'Confirmer'
                    )}
                  </Button>
                  <Button
                    outline
                    onClick={() => {
                      setIsChangingPassword(false);
                      setPasswordData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
                      setPasswordError('');
                    }}
                    disabled={isUpdatingPassword}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour afficher un champ d'information
const InfoField: FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
}> = ({ icon, label, value, highlight }) => (
  <div className="flex gap-3">
    <div className={`w-10 h-10 ${highlight ? 'bg-primary/10' : 'bg-slate-100'} rounded-lg flex items-center justify-center flex-shrink-0`}>
      <div className={highlight ? 'text-primary' : 'text-slate-600'}>
        {icon}
      </div>
    </div>
    <div className="flex-1 min-w-0">
      <label className="block text-xs font-medium text-gray-500 mb-1">
        {label}
      </label>
      <div className={`${highlight ? 'text-gray-900 font-semibold' : 'text-gray-900'} break-words`}>
        {value}
      </div>
    </div>
  </div>
);

export default UserAccount;
